/**
 * TarotReading — interactive in-chat reading flow.
 *
 * Steps:
 *   0. Profile gate     (conditional: only if querentName has no profile)
 *   1. Theme + question (theme tile + optional free-text)
 *   2. Spread picker    (recommended spreads for theme shown first)
 *   3. Laydown guide    (SpreadDiagram with numbered slots + position list)
 *   4. Card input       (per position: suit + rank picker + reversed toggle)
 *   5. Rendered reading (per-position interpretations + observations
 *                        + streamed LLM synthesis + SpreadDiagram with real cards)
 *   6. Save / end       (terminal — calls onComplete with the wrap-up)
 *
 * The component talks to /api/tools/tarot/* directly — no LLM turns are
 * burned during the in-chat flow. When the user picks save or end, we
 * call onComplete with the wrap-up display string, which the chat host
 * appends as a normal bilbo bubble.
 */

import { useEffect, useMemo, useState } from 'react';
import './TarotReading.css';
import { SpreadDiagram } from './SpreadDiagram';
import type { DrawnSlot } from './SpreadDiagram';
import type { RenderComponentProps } from '../render-registry';

interface SpreadPosition {
  index: number;
  label: string;
  meaning: string;
  layoutHint?: { x: number; y: number; rotation?: number };
}
interface Spread {
  id: string;
  name: string;
  description: string;
  themes: string[];
  positions: SpreadPosition[];
}
interface CardCatalogEntry {
  id: string;
  name: string;
  arcana: 'major' | 'minor';
  suit: 'wands' | 'cups' | 'swords' | 'pentacles' | null;
  rank: string;
  imageUrl: string;
}
interface Drawn {
  positionIndex: number;
  cardId: string | null;
  reversed: boolean;
}
interface Position {
  positionIndex: number;
  positionLabel: string;
  positionMeaning: string;
  cardId: string;
  cardName: string;
  reversed: boolean;
  themeUsed: string;
  text: string;
  chartContext?: string | null;
}
interface Observation {
  type: string;
  [k: string]: unknown;
}
interface ReadingPacket {
  spreadId: string;
  spreadName: string;
  theme: string;
  question: string | null;
  positions: Position[];
  observations: Observation[];
}
interface ChartPlacement {
  sign: string;
  degree: number;
  house?: number;
  retrograde?: boolean;
}
interface NatalChart {
  sun: ChartPlacement;
  moon?: ChartPlacement;
  rising?: string;
  planets: Record<string, ChartPlacement | undefined>;
  houses?: string[];
}
interface CityMatch {
  name: string;
  country: string;
  lat: number;
  lng: number;
  tz: string;
}

const THEMES = ['general', 'love', 'eroticism', 'family', 'friends', 'career', 'finances', 'health', 'feelings', 'spirituality', 'growth', 'actions', 'timing'] as const;
type Theme = (typeof THEMES)[number];

const THEME_LABEL: Record<Theme, string> = {
  general: 'General', love: 'Love', eroticism: 'Eroticism', family: 'Family & Home', friends: 'Friends & Community',
  career: 'Career', finances: 'Finances', health: 'Health & Body', feelings: 'Feelings', spirituality: 'Spirituality',
  growth: 'Personal Growth', actions: 'Actions', timing: 'Timing',
};
const THEME_GROUPS: { label: string; themes: Theme[] }[] = [
  { label: 'Overview', themes: ['general'] },
  { label: 'Relationships', themes: ['love', 'eroticism', 'family', 'friends'] },
  { label: 'Work & Money', themes: ['career', 'finances'] },
  { label: 'Mind & Body', themes: ['health', 'feelings', 'spirituality', 'growth'] },
  { label: 'Guidance', themes: ['actions', 'timing'] },
];

interface TarotProps {
  sessionId: number;
  mode?: 'reading' | 'view';
  spreadId?: string | null;
  theme?: string | null;
  question?: string | null;
  querentName?: string | null;
  profileMissing?: boolean;
  /** Present only when the LLM called `resumeTarotReading` — carries the persisted state we hydrate from. */
  resume?: {
    cards: Drawn[] | null;
    reading: ReadingPacket | null;
    synthesis: string | null;
  };
}

interface SavedSession {
  id: number;
  spreadId: string;
  theme: string;
  question: string | null;
  synthesis: string | null;
  notes: string | null;
  reading: ReadingPacket;
  chart?: NatalChart | null;
  chartSummary?: string | null;
}

type Step = 'profile-gate' | 'theme' | 'spread' | 'laydown' | 'cards' | 'reading' | 'wrap';

function initialStep(props: TarotProps): Step {
  if (props.mode === 'view') return 'theme'; // unused in view mode
  if (props.profileMissing) return 'profile-gate';
  if (props.resume) {
    // Resume hydration ladder. Order matters: most-progressed state first.
    // Matches the resumeTarotReading tool's documented contract:
    //   reading_json non-null → reading step (synthesis may still need streaming)
    //   cards_json non-null   → reading step (engine will re-derive the packet)
    //   spread_id non-null    → cards step (skip the laydown guide on resume)
    //   theme non-null        → spread step (theme picked, spread not)
    //   nothing picked yet    → theme step
    if (props.resume.reading) return 'reading';
    if (props.resume.cards && props.resume.cards.length > 0) return 'reading';
    if (props.spreadId) return 'cards';
    if (props.theme) return 'spread';
    return 'theme';
  }
  return 'theme';
}

export function TarotReading({ props, onComplete, onDismiss }: RenderComponentProps<TarotProps>) {
  const { sessionId, profileMissing, querentName } = props;

  const [step, setStep] = useState<Step>(() => initialStep(props));
  const [spreads, setSpreads] = useState<Spread[] | null>(null);
  const [cards, setCards] = useState<CardCatalogEntry[] | null>(null);
  const [pickedSpread, setPickedSpread] = useState<Spread | null>(null);
  const [theme, setTheme] = useState<Theme>(((props.theme as Theme) ?? 'general'));
  const [question, setQuestion] = useState(props.question ?? '');
  const [drawn, setDrawn] = useState<Drawn[]>(props.resume?.cards ?? []);
  const [reading, setReading] = useState<ReadingPacket | null>(props.resume?.reading ?? null);
  const [synthesis, setSynthesis] = useState(props.resume?.synthesis ?? '');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewSession, setViewSession] = useState<SavedSession | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [chart, setChart] = useState<NatalChart | null>(null);
  const [chartLine, setChartLine] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);

  // Fetch catalog once.
  useEffect(() => {
    void fetch('/api/tools/tarot/spreads')
      .then((r) => r.json())
      .then((j) => setSpreads(j.spreads as Spread[]))
      .catch(() => setError('Could not load spreads.'));
    void fetch('/api/tools/tarot/cards')
      .then((r) => r.json())
      .then((j) => setCards(j.cards as CardCatalogEntry[]))
      .catch(() => setError('Could not load card catalog.'));
  }, []);

  // View mode: load the saved session once.
  useEffect(() => {
    if (props.mode !== 'view') return;
    void fetch(`/api/tools/tarot/sessions/${sessionId}`)
      .then((r) => {
        if (!r.ok) throw new Error('fetch_failed');
        return r.json();
      })
      .then((j: { session: SavedSession }) => {
        setViewSession(j.session);
        setNotes(j.session.notes ?? '');
      })
      .catch(() => setError('Could not load reading.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the LLM pre-filled a spread, hydrate pickedSpread once the catalogue lands.
  // If we're already on the spread step, auto-advance to laydown.
  useEffect(() => {
    if (!pickedSpread && spreads && props.spreadId) {
      const match = spreads.find((s) => s.id === props.spreadId);
      if (match) {
        setPickedSpread(match);
        if (drawn.length === 0) {
          setDrawn(match.positions.map((p) => ({ positionIndex: p.index, cardId: null, reversed: false })));
        }
        if (step === 'spread') setStep('laydown');
      }
    }
  }, [spreads, pickedSpread, props.spreadId, step, drawn.length]);

  // On resume: re-fetch the reading packet if we have cards but no reading,
  // and re-stream synthesis if we have a reading but no synthesis.
  useEffect(() => {
    if (!props.resume) return;
    if (step !== 'reading') return;
    if (!reading) {
      void (async () => {
        try {
          const r = await fetch(`/api/tools/tarot/sessions/${sessionId}/reading`);
          if (!r.ok) throw new Error('reading_failed');
          const j = (await r.json()) as {
            reading: ReadingPacket;
            chart?: NatalChart | null;
            chartSummary?: string | null;
          };
          setReading(j.reading);
          setChart(j.chart ?? null);
          setChartLine(j.chartSummary ?? null);
          if (!synthesis) void startSynthesisStream();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'unknown_error');
        }
      })();
    } else if (!synthesis) {
      void startSynthesisStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardsById = useMemo(() => {
    if (!cards) return new Map<string, CardCatalogEntry>();
    return new Map(cards.map((c) => [c.id, c]));
  }, [cards]);

  function pickSpread(s: Spread) {
    setPickedSpread(s);
    setDrawn(s.positions.map((p) => ({ positionIndex: p.index, cardId: null, reversed: false })));
    setStep('laydown');
  }

  async function createStubProfile(
    create: boolean,
    fields?: { birthDate?: string; birthName?: string; birthTime?: string; birthLocation?: string },
  ) {
    if (!querentName) {
      setStep('theme');
      return;
    }
    try {
      const res = await fetch('/api/tools/tarot/profiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: querentName,
          birthDate: create ? fields?.birthDate ?? null : null,
          birthName: create ? fields?.birthName ?? null : null,
          birthTime: create ? fields?.birthTime ?? null : null,
          birthLocation: create && fields?.birthLocation ? fields.birthLocation : null,
        }),
      });
      if (!res.ok && res.status !== 409) {
        setError('Could not create profile; continuing without one.');
      }
    } catch {
      setError('Could not create profile; continuing without one.');
    }
    setStep('theme');
  }

  async function submitCards() {
    if (!pickedSpread) return;
    const complete = drawn.filter((d) => d.cardId != null);
    if (complete.length !== pickedSpread.positions.length) {
      setError('Pick a card for every position first.');
      return;
    }
    setError(null);
    try {
      const cardsRes = await fetch(`/api/tools/tarot/sessions/${sessionId}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spreadId: pickedSpread.id,
          theme,
          question: question.trim() || null,
          cards: complete.map((d) => ({
            positionIndex: d.positionIndex,
            cardId: d.cardId,
            reversed: d.reversed,
          })),
        }),
      });
      if (!cardsRes.ok) throw new Error('cards_failed');

      const readingRes = await fetch(`/api/tools/tarot/sessions/${sessionId}/reading`);
      if (!readingRes.ok) throw new Error('reading_failed');
      const readingJson = (await readingRes.json()) as {
        reading: ReadingPacket;
        chart?: NatalChart | null;
        chartSummary?: string | null;
      };
      setReading(readingJson.reading);
      setChart(readingJson.chart ?? null);
      setChartLine(readingJson.chartSummary ?? null);
      setStep('reading');
      void startSynthesisStream();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown_error');
    }
  }

  async function startSynthesisStream() {
    setSynthesis('');
    setStreaming(true);
    try {
      const res = await fetch(`/api/tools/tarot/sessions/${sessionId}/synthesis`);
      if (!res.ok || !res.body) throw new Error('synthesis_failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const evt of events) {
          const line = evt.replace(/^data:\s*/, '');
          if (!line) continue;
          try {
            const parsed = JSON.parse(line) as {
              delta?: string;
              done?: boolean;
              error?: string;
              chartContexts?: Record<number, string | null>;
            };
            if (parsed.chartContexts) {
              // Annotate positions with chart context lines.
              setReading((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  positions: prev.positions.map((p) => {
                    const ctx = parsed.chartContexts![p.positionIndex];
                    return ctx !== undefined ? { ...p, chartContext: ctx } : p;
                  }),
                };
              });
            }
            if (parsed.delta) setSynthesis((prev) => prev + parsed.delta);
            if (parsed.error) setError(parsed.error);
          } catch {
            // ignore malformed chunk
          }
        }
      }
    } catch {
      setError('Synthesis stream failed.');
    } finally {
      setStreaming(false);
    }
  }

  async function saveNotes(value: string) {
    await fetch(`/api/tools/tarot/sessions/${sessionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: value || null }),
    }).catch(() => null);
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  }

  async function save() {
    if (!reading || !pickedSpread) return;
    await fetch(`/api/tools/tarot/sessions/${sessionId}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: question.trim() || null }),
    });
    const lines: string[] = [`✓ Tarot reading saved · ${pickedSpread.name} · ${theme}`];
    for (const p of reading.positions) {
      const rev = p.reversed ? ' (reversed)' : '';
      lines.push(`   • ${p.positionLabel}: ${p.cardName}${rev}`);
    }
    onComplete(lines.join('\n'));
  }

  async function endWithoutSaving() {
    await fetch(`/api/tools/tarot/sessions/${sessionId}/end`, { method: 'POST' });
    onComplete('✓ Tarot reading ended (not saved).');
  }

  async function doDelete() {
    try {
      const res = await fetch(`/api/tools/tarot/sessions/${sessionId}`, { method: 'DELETE' });
      if (res.status === 404) {
        setError('Reading not found.');
        setConfirmDelete(false);
        return;
      }
      if (!res.ok) throw new Error('delete_failed');
      onDismiss('✓ Reading deleted.');
    } catch {
      setError('Could not delete reading.');
      setConfirmDelete(false);
    }
  }

  // ---------- view mode ----------

  if (props.mode === 'view') {
    if (!viewSession || !spreads || !cards) {
      return <p className="tarot-loading">{error ?? 'Loading reading…'}</p>;
    }
    const viewSpread = spreads.find((s) => s.id === viewSession.spreadId);
    if (!viewSpread) return <p className="tarot-error">Unknown spread.</p>;

    const diagramCards: DrawnSlot[] = viewSession.reading.positions
      .map((p) => {
        const card = cardsById.get(p.cardId);
        if (!card) return null;
        return { positionIndex: p.positionIndex, imageUrl: card.imageUrl, reversed: p.reversed };
      })
      .filter((x): x is DrawnSlot => x != null);

    return (
      <div className="tarot-step">
        <div className="tarot-reading-header">
          <h3 className="tarot-step-title">
            {viewSpread.name} · {viewSession.theme}
          </h3>
          {viewSession.question && <p className="tarot-question-echo">"{viewSession.question}"</p>}
          {viewSession.chartSummary && <ChartSummary line={viewSession.chartSummary} chart={viewSession.chart ?? null} />}
        </div>
        <SpreadDiagram spread={viewSpread} cards={diagramCards} />
        <div className="tarot-positions">
          {viewSession.reading.positions.map((p) => {
            const card = cardsById.get(p.cardId);
            return (
              <div key={p.positionIndex} className="tarot-position">
                {card && (
                  <img
                    src={card.imageUrl}
                    alt={p.cardName}
                    className={`tarot-card-img${p.reversed ? ' is-reversed' : ''}`}
                  />
                )}
                <div className="tarot-position-body">
                  <div className="tarot-position-label">
                    {p.positionLabel} — {p.cardName}
                    {p.reversed ? ' (reversed)' : ''}
                  </div>
                  <p className="tarot-position-text">{p.text}</p>
                  {p.chartContext && (
                    <p className="tarot-chart-context">{p.chartContext}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {viewSession.reading.observations.length > 0 && (
          <ul className="tarot-observations">
            {viewSession.reading.observations.map((o, i) => (
              <li key={i}>{describeObservation(o)}</li>
            ))}
          </ul>
        )}
        <div className="tarot-synthesis">
          <h4>Synthesis</h4>
          <p>{viewSession.synthesis || '(no synthesis)'}</p>
        </div>
        <div className="tarot-notes">
          <label className="tarot-notes-label">
            Your take{notesSaved ? <span className="tarot-notes-saved"> · saved</span> : null}
          </label>
          <textarea
            className="tarot-notes-input"
            rows={3}
            placeholder="Personal notes on this reading…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={(e) => void saveNotes(e.target.value)}
          />
        </div>
        {confirmDelete ? (
          <div className="tarot-confirm">
            <p className="tarot-confirm-text">Delete this reading? This can't be undone.</p>
            <div className="tarot-actions">
              <button className="tarot-btn tarot-btn--danger" onClick={() => void doDelete()}>Delete</button>
              <button className="tarot-btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="tarot-actions">
            <button className="tarot-btn-ghost" onClick={() => onDismiss()}>Close</button>
            <button className="tarot-btn tarot-btn--danger" onClick={() => setConfirmDelete(true)}>Delete</button>
          </div>
        )}
        {error && <p className="tarot-error">{error}</p>}
      </div>
    );
  }

  // ---------- renderers per step ----------

  if (step === 'profile-gate') {
    return <ProfileGate querentName={querentName ?? '?'} onAnswer={createStubProfile} error={error} />;
  }

  if (step === 'theme') {
    return (
      <div className="tarot-step">
        <h3 className="tarot-step-title">Theme + question.</h3>
        <div className="tarot-theme-groups">
          {THEME_GROUPS.map((group) => (
            <div key={group.label} className="tarot-theme-group">
              <span className="tarot-theme-group-label">{group.label}</span>
              <div className="tarot-themes">
                {group.themes.map((t) => (
                  <button
                    key={t}
                    className={`tarot-theme-pill${theme === t ? ' is-active' : ''}`}
                    onClick={() => setTheme(t)}
                  >
                    {THEME_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <textarea
          className="tarot-question"
          placeholder="Optional question…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={2}
        />
        <div className="tarot-actions">
          <button className="tarot-btn" onClick={() => setStep('spread')}>Continue</button>
          <button className="tarot-btn-ghost" onClick={endWithoutSaving}>End</button>
        </div>
      </div>
    );
  }

  if (step === 'spread') {
    if (!spreads) return <p className="tarot-loading">Loading spreads…</p>;
    const recommended = spreads.filter((s) => s.themes.includes(theme));
    const others = spreads.filter((s) => !s.themes.includes(theme));
    return (
      <div className="tarot-step">
        <h3 className="tarot-step-title">Pick a spread.</h3>
        {recommended.length > 0 && (
          <>
            <p className="tarot-spread-section">Recommended for {theme}</p>
            <div className="tarot-grid">
              {recommended.map((s) => (
                <button key={s.id} className="tarot-tile tarot-tile--recommended" onClick={() => pickSpread(s)}>
                  <div className="tarot-tile-name">{s.name}</div>
                  <div className="tarot-tile-detail">{s.positions.length} cards</div>
                </button>
              ))}
            </div>
          </>
        )}
        {others.length > 0 && (
          <>
            <p className="tarot-spread-section tarot-spread-section--all">All spreads</p>
            <div className="tarot-grid">
              {others.map((s) => (
                <button key={s.id} className="tarot-tile tarot-tile--other" onClick={() => pickSpread(s)}>
                  <div className="tarot-tile-name">{s.name}</div>
                  <div className="tarot-tile-detail">{s.positions.length} cards</div>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="tarot-actions">
          <button className="tarot-btn-ghost" onClick={() => setStep('theme')}>Back</button>
          <button className="tarot-btn-ghost" onClick={endWithoutSaving}>End</button>
        </div>
      </div>
    );
  }

  if (step === 'laydown') {
    if (!pickedSpread) return null;
    return (
      <div className="tarot-step">
        <h3 className="tarot-step-title">Lay your cards.</h3>
        <SpreadDiagram spread={pickedSpread} />
        <ol className="tarot-laydown-list">
          {pickedSpread.positions.map((p) => (
            <li key={p.index}>
              <span className="tarot-laydown-label">{p.label}</span>
              <span className="tarot-laydown-meaning"> — {p.meaning}</span>
            </li>
          ))}
        </ol>
        <p className="tarot-help">Lay your cards in this order, then click 'Cards are down' to enter what you drew.</p>
        <div className="tarot-actions">
          <button className="tarot-btn" onClick={() => setStep('cards')}>Cards are down</button>
          <button className="tarot-btn-ghost" onClick={() => setStep('spread')}>Back</button>
        </div>
      </div>
    );
  }

  if (step === 'cards') {
    if (!pickedSpread || !cards) return <p className="tarot-loading">Loading cards…</p>;
    return (
      <CardInput
        spread={pickedSpread}
        catalog={cards}
        drawn={drawn}
        setDrawn={setDrawn}
        onSubmit={submitCards}
        onBack={() => setStep('laydown')}
        onEnd={endWithoutSaving}
        error={error}
      />
    );
  }

  if (step === 'reading') {
    if (!reading || !pickedSpread) return <p className="tarot-loading">Building reading…</p>;

    const diagramCards: DrawnSlot[] = reading.positions
      .map((p) => {
        const card = cardsById.get(p.cardId);
        if (!card) return null;
        return { positionIndex: p.positionIndex, imageUrl: card.imageUrl, reversed: p.reversed };
      })
      .filter((x): x is DrawnSlot => x != null);

    return (
      <div className="tarot-step">
        <div className="tarot-reading-header">
          <h3 className="tarot-step-title">
            {pickedSpread.name} · {theme}
          </h3>
          {reading.question && <p className="tarot-question-echo">"{reading.question}"</p>}
          {chartLine && <ChartSummary line={chartLine} chart={chart} />}
        </div>
        <SpreadDiagram spread={pickedSpread} cards={diagramCards} />
        <div className="tarot-positions">
          {reading.positions.map((p) => {
            const card = cardsById.get(p.cardId);
            return (
              <div key={p.positionIndex} className="tarot-position">
                {card && (
                  <img
                    src={card.imageUrl}
                    alt={p.cardName}
                    className={`tarot-card-img${p.reversed ? ' is-reversed' : ''}`}
                  />
                )}
                <div className="tarot-position-body">
                  <div className="tarot-position-label">
                    {p.positionLabel} — {p.cardName}
                    {p.reversed ? ' (reversed)' : ''}
                  </div>
                  <p className="tarot-position-text">{p.text}</p>
                  {p.chartContext && (
                    <p className="tarot-chart-context">{p.chartContext}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {reading.observations.length > 0 && (
          <ul className="tarot-observations">
            {reading.observations.map((o, i) => (
              <li key={i}>{describeObservation(o)}</li>
            ))}
          </ul>
        )}
        <div className="tarot-synthesis">
          <h4>Synthesis</h4>
          <p>
            {synthesis || (streaming ? '…' : '(no synthesis yet)')}
            {streaming && <span className="tarot-cursor">▍</span>}
          </p>
        </div>
        <div className="tarot-notes">
          <label className="tarot-notes-label">
            Your take{notesSaved ? <span className="tarot-notes-saved"> · saved</span> : null}
          </label>
          <textarea
            className="tarot-notes-input"
            rows={3}
            placeholder="Personal notes on this reading…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={(e) => void saveNotes(e.target.value)}
          />
        </div>
        <div className="tarot-actions">
          <button className="tarot-btn" onClick={save} disabled={streaming}>
            Save reading
          </button>
          <button className="tarot-btn-ghost" onClick={endWithoutSaving} disabled={streaming}>
            End without saving
          </button>
        </div>
        {error && <p className="tarot-error">{error}</p>}
      </div>
    );
  }

  return null;
}

// ---------- profile gate ----------

function ProfileGate({
  querentName,
  onAnswer,
  error,
}: {
  querentName: string;
  onAnswer: (
    create: boolean,
    fields?: { birthDate?: string; birthName?: string; birthTime?: string; birthLocation?: string },
  ) => void;
  error: string | null;
}) {
  const [mode, setMode] = useState<'ask' | 'create'>('ask');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthName, setBirthName] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [picked, setPicked] = useState<CityMatch | null>(null);

  if (mode === 'ask') {
    return (
      <div className="tarot-step">
        <h3 className="tarot-step-title">No profile for {querentName}.</h3>
        <p className="tarot-help">A birth date gives numerology; add time and place for the full natal chart. Create the profile now or skip and read without it. Either way a stub gets saved so you can fill it in later.</p>
        <div className="tarot-actions">
          <button className="tarot-btn" onClick={() => setMode('create')}>Yes, create now</button>
          <button className="tarot-btn-ghost" onClick={() => onAnswer(false)}>Skip</button>
        </div>
        {error && <p className="tarot-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="tarot-step">
      <h3 className="tarot-step-title">Profile · {querentName}</h3>
      <label className="tarot-field">
        Birth date
        <input
          type="date"
          value={birthDate}
          onChange={(e) => setBirthDate(e.target.value)}
        />
      </label>
      <label className="tarot-field">
        Birth time <span className="tarot-field-note">(optional — enables Moon + Rising)</span>
        <input
          type="time"
          value={birthTime}
          onChange={(e) => setBirthTime(e.target.value)}
        />
      </label>
      <CityPicker
        label="Birth place"
        helpText="optional — enables houses + Rising"
        query={locationQuery}
        setQuery={setLocationQuery}
        picked={picked}
        setPicked={setPicked}
      />
      <label className="tarot-field">
        Full birth name <span className="tarot-field-note">(for Pythagorean numerology — optional)</span>
        <input type="text" value={birthName} onChange={(e) => setBirthName(e.target.value)} />
      </label>
      <div className="tarot-actions">
        <button
          className="tarot-btn"
          onClick={() =>
            onAnswer(true, {
              birthDate: birthDate || undefined,
              birthTime: birthTime || undefined,
              birthLocation: picked ? `${picked.name}, ${picked.country}` : undefined,
              birthName: birthName || undefined,
            })
          }
        >
          Save and continue
        </button>
        <button className="tarot-btn-ghost" onClick={() => onAnswer(false)}>Skip</button>
      </div>
      {error && <p className="tarot-error">{error}</p>}
    </div>
  );
}

function CityPicker({
  label,
  helpText,
  query,
  setQuery,
  picked,
  setPicked,
}: {
  label: string;
  helpText: string;
  query: string;
  setQuery: (s: string) => void;
  picked: CityMatch | null;
  setPicked: (c: CityMatch | null) => void;
}) {
  const [matches, setMatches] = useState<CityMatch[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (picked) return;
    const q = query.trim();
    if (q.length < 2) {
      setMatches([]);
      return;
    }
    const t = setTimeout(() => {
      void fetch(`/api/tools/tarot/cities?q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((j: { cities: CityMatch[] }) => setMatches(j.cities))
        .catch(() => setMatches([]));
    }, 150);
    return () => clearTimeout(t);
  }, [query, picked]);

  return (
    <label className="tarot-field">
      {label} <span className="tarot-field-note">({helpText})</span>
      {picked ? (
        <div className="tarot-city-picked">
          <span>{picked.name}, {picked.country}</span>
          <button
            type="button"
            className="tarot-btn-link"
            onClick={() => {
              setPicked(null);
              setQuery('');
            }}
          >
            change
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            placeholder="Start typing a city…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            autoComplete="off"
          />
          {open && matches.length > 0 && (
            <ul className="tarot-city-suggestions">
              {matches.map((c) => (
                <li key={`${c.name}|${c.country}|${c.lat}|${c.lng}`}>
                  <button
                    type="button"
                    className="tarot-city-suggestion"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setPicked(c);
                      setQuery('');
                    }}
                  >
                    {c.name}, {c.country}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </label>
  );
}

// ---------- card input ----------

const MAJOR_IDS_ORDER = (cat: CardCatalogEntry[]) =>
  cat
    .filter((c) => c.arcana === 'major')
    .sort((a, b) => Number(a.rank) - Number(b.rank));

const SUIT_ORDER: Array<'wands' | 'cups' | 'swords' | 'pentacles'> = ['wands', 'cups', 'swords', 'pentacles'];
const RANK_ORDER = ['ace', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'page', 'knight', 'queen', 'king'];

function CardInput({
  spread,
  catalog,
  drawn,
  setDrawn,
  onSubmit,
  onBack,
  onEnd,
  error,
}: {
  spread: Spread;
  catalog: CardCatalogEntry[];
  drawn: Drawn[];
  setDrawn: (d: Drawn[]) => void;
  onSubmit: () => void;
  onBack: () => void;
  onEnd: () => void;
  error: string | null;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [pickerMode, setPickerMode] = useState<'majors' | 'wands' | 'cups' | 'swords' | 'pentacles'>('majors');

  const cardsById = useMemo(() => new Map(catalog.map((c) => [c.id, c])), [catalog]);
  const majors = useMemo(() => MAJOR_IDS_ORDER(catalog), [catalog]);
  const minorsBySuit = useMemo(() => {
    const m = new Map<string, CardCatalogEntry[]>();
    for (const c of catalog) {
      if (c.arcana === 'minor' && c.suit) {
        const list = m.get(c.suit) ?? [];
        list.push(c);
        m.set(c.suit, list);
      }
    }
    for (const list of m.values()) {
      list.sort((a, b) => RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank));
    }
    return m;
  }, [catalog]);

  function setCard(positionIndex: number, cardId: string) {
    const newDrawn = drawn.map((d) => (d.positionIndex === positionIndex ? { ...d, cardId } : d));
    setDrawn(newDrawn);
    // Auto-advance to next empty slot in index order after the current slot.
    const currentIdx = drawn.findIndex((d) => d.positionIndex === positionIndex);
    const nextEmpty = newDrawn.findIndex((d, i) => i > currentIdx && d.cardId === null);
    if (nextEmpty !== -1) setActiveIndex(nextEmpty);
  }
  function toggleReversed(positionIndex: number) {
    setDrawn(drawn.map((d) => (d.positionIndex === positionIndex ? { ...d, reversed: !d.reversed } : d)));
  }

  // Cards placed in OTHER slots are disabled (one physical card per deck).
  const activePositionIndex = drawn[activeIndex]?.positionIndex;
  const usedCardIds = new Set(
    drawn
      .filter((d) => d.positionIndex !== activePositionIndex && d.cardId !== null)
      .map((d) => d.cardId as string),
  );

  const visibleCards = pickerMode === 'majors' ? majors : minorsBySuit.get(pickerMode) ?? [];

  return (
    <div className="tarot-step">
      <h3 className="tarot-step-title">Pick cards.</h3>
      <div className="tarot-position-tabs">
        {spread.positions.map((p, i) => {
          const d = drawn[i]!;
          const card = d.cardId ? cardsById.get(d.cardId) : null;
          return (
            <button
              key={p.index}
              className={`tarot-position-tab${activeIndex === i ? ' is-active' : ''}`}
              onClick={() => setActiveIndex(i)}
            >
              <div className="tarot-position-tab-label">{p.label}</div>
              <div className="tarot-position-tab-card">
                {card ? `${card.name}${d.reversed ? ' (R)' : ''}` : '—'}
              </div>
            </button>
          );
        })}
      </div>

      <div className="tarot-picker-modes">
        <button
          className={`tarot-mode-pill${pickerMode === 'majors' ? ' is-active' : ''}`}
          onClick={() => setPickerMode('majors')}
        >
          Majors
        </button>
        {SUIT_ORDER.map((s) => (
          <button
            key={s}
            className={`tarot-mode-pill${pickerMode === s ? ' is-active' : ''}`}
            onClick={() => setPickerMode(s)}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="tarot-card-grid">
        {visibleCards.map((c) => {
          const chosen = drawn[activeIndex]?.cardId === c.id;
          const isUsed = usedCardIds.has(c.id);
          return (
            <button
              key={c.id}
              className={`tarot-card-pick${chosen ? ' is-chosen' : ''}${isUsed ? ' is-used' : ''}`}
              disabled={isUsed}
              onClick={() => setCard(spread.positions[activeIndex]!.index, c.id)}
            >
              <img src={c.imageUrl} alt={c.name} />
              <div>{c.name}</div>
            </button>
          );
        })}
        {visibleCards.length === 0 && (
          <p className="tarot-empty">No cards in dataset for this group yet.</p>
        )}
      </div>

      <label className="tarot-reversed">
        <input
          type="checkbox"
          checked={!!drawn[activeIndex]?.reversed}
          onChange={() => toggleReversed(spread.positions[activeIndex]!.index)}
        />
        Reversed
      </label>

      <div className="tarot-actions">
        <button className="tarot-btn" onClick={onSubmit}>Read the spread</button>
        <button className="tarot-btn-ghost" onClick={onBack}>Back</button>
        <button className="tarot-btn-ghost" onClick={onEnd}>End</button>
      </div>
      {error && <p className="tarot-error">{error}</p>}
    </div>
  );
}

// ---------- helpers ----------

function describeObservation(o: Observation): string {
  switch (o.type) {
    case 'suit-dominance':
      return `Suit dominance — ${o.count} ${o.suit} (${o.label}).`;
    case 'majors-density':
      return `Majors density — ${o.count}/${o.total} (${o.label}).`;
    case 'elemental-dignity':
      return `Elemental dignity (${(o.positions as number[]).join('→')}) — ${o.effect}.`;
    case 'number-pattern':
      return `Pattern — ${o.pattern} ×${o.count}: ${o.note}.`;
    case 'court-roles':
      return `Court roles present — ${Object.entries(o.counts as Record<string, number>)
        .map(([k, v]) => `${v} ${k}`)
        .join(', ')}.`;
    case 'numerology-match':
      return `Numerology match — ${o.drawn} is ${o.profile}'s ${o.role} card.`;
    case 'famous-pair':
      return `Famous pair — ${(o.cards as string[]).join(' + ')}: ${o.note}`;
    case 'natal-resonance':
      return `Natal resonance — ${o.card}: ${o.reason}.`;
    case 'aspect-resonance':
      return `Aspect resonance — ${o.card}: ${o.reason as string}.`;
    default:
      return JSON.stringify(o);
  }
}

const PLANET_ORDER = ['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'];

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ChartSummary({ line, chart }: { line: string; chart: NatalChart | null }) {
  const [open, setOpen] = useState(false);
  const placements = chart
    ? PLANET_ORDER
        .map((p) => ({ planet: p, placement: chart.planets[p] }))
        .filter((x): x is { planet: string; placement: ChartPlacement } => !!x.placement)
    : [];
  return (
    <div className="tarot-chart-summary">
      <span className="tarot-chart-line">{line}</span>
      {chart && placements.length > 1 && (
        <button
          type="button"
          className="tarot-btn-link"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'hide chart' : 'show full chart'}
        </button>
      )}
      {open && chart && (
        <ul className="tarot-chart-detail">
          {placements.map(({ planet, placement }) => (
            <li key={planet}>
              <span className="tarot-chart-planet">{titleCase(planet)}</span>
              {' '}in {titleCase(placement.sign)} {placement.degree.toFixed(1)}°
              {placement.house != null && ` · house ${placement.house}`}
              {placement.retrograde && ' · ℞'}
            </li>
          ))}
          {chart.rising && (
            <li><span className="tarot-chart-planet">Asc</span> in {titleCase(chart.rising)}</li>
          )}
        </ul>
      )}
    </div>
  );
}
